import { useCallback, useSyncExternalStore } from "react";
import { router } from "../router.js";

/**
 * Filter state lives in the URL, not in a component (AGENTS.md §15a).
 *
 * The reason is not tidiness. A filtered view someone can send to a colleague,
 * bookmark, or reload without losing is a different product from one where the
 * back button throws the filters away — and there is no way to get that from
 * `useState`.
 *
 * Reads and writes go through the router's history, never `window.history`.
 * The router keeps its own record of where it is and moves the scroll by it; a
 * write behind its back used to leave it believing it was still on the old
 * address. And its record updates at once, while the address bar is written a
 * microtask later — so reading `window.location` straight after a write would
 * read the page before it.
 *
 * `useSyncExternalStore` rather than an effect: the URL is external state, and
 * subscribing to it is exactly what that hook is for. An effect here would tear
 * — React could render one set of filters while the address bar showed another.
 *
 * The router is only touched inside functions, never at import: it imports the
 * screens that import this file.
 */

function subscribe(onChange: () => void): () => void {
  return router.history.subscribe(onChange);
}

function currentSearch(): string {
  return router.history.location.search;
}

function currentPath(): string {
  return router.history.location.pathname;
}

/** Which address is showing. */
export function usePathname(): string {
  return useSyncExternalStore(subscribe, currentPath, () => "/");
}

/**
 * Moves to another screen, dropping the filters.
 *
 * A new page opens at the top; the router puts the old one's position back if
 * the reader returns with Back.
 */
export function navigate(
  path: string,
  options: { readonly replace?: boolean } = {},
): void {
  void router.navigate({ href: path, replace: options.replace ?? false });
}

/**
 * A screen's path, for one brand's report.
 *
 * `navigate` drops the querystring on purpose, so filters do not leak between
 * screens. The report is not a filter: it is what the screen is *about*, and a
 * link without it lands on the first brand. Every link to a brand's screen goes
 * through here.
 */
export function withReport(href: string, reportId: string): string {
  return `${href}?report=${encodeURIComponent(reportId)}`;
}

/**
 * Makes the address bar name the report a brand screen is showing, when it
 * did not — a bare `/overview`, `/`, or a report that no longer exists.
 *
 * A screen that quietly picked a brand the address never named showed one
 * company under a link that could mean any of them. Replaced rather than
 * pushed: it is the same page, now saying which brand, so Back does not return
 * to the unnamed address. The filters in the querystring are kept, and so is
 * the scroll.
 */
export function nameReportInUrl(path: string, reportId: string): void {
  // Only for the screen the address is on (or home, which becomes the
  // Overview). The address moves before the screen does, so for one render a
  // screen being left still asks to be named — and obeying it dragged the
  // reader back from where they had just clicked to.
  const here = currentPath();
  if (here !== path && here !== "/") return;
  const params = new URLSearchParams(currentSearch());
  if (currentPath() === path && params.get("report") === reportId) return;
  params.set("report", reportId);
  void router.navigate({
    href: `${path}?${params.toString()}`,
    replace: true,
    resetScroll: false,
  });
}

/**
 * The querystring, and a setter that writes one key.
 *
 * Values are `string | undefined`, never `""`. An empty string in a URL is a
 * filter set to nothing, which is not the same as no filter — and the API
 * schema would reject it. Setting a key to undefined removes it.
 */
export function useUrlFilters(): {
  readonly params: URLSearchParams;
  readonly setParam: (key: string, value: string | undefined) => void;
  /** Several keys in one history entry, so Back undoes them together. */
  readonly setParams: (values: Readonly<Record<string, string | undefined>>) => void;
  readonly clearAll: () => void;
} {
  const search = useSyncExternalStore(subscribe, currentSearch, () => "");

  const write = useCallback((next: URLSearchParams) => {
    const query = next.toString();
    // Pushed, not replaced: each filter change is somewhere Back should return
    // to. And the scroll stays — narrowing a list is not leaving the page.
    void router.navigate({
      href: `${currentPath()}${query === "" ? "" : `?${query}`}`,
      resetScroll: false,
    });
  }, []);

  const setParam = useCallback(
    (key: string, value: string | undefined) => {
      const next = new URLSearchParams(currentSearch());
      if (value === undefined || value === "") next.delete(key);
      else next.set(key, value);
      write(next);
    },
    [write],
  );

  const setParams = useCallback(
    (values: Readonly<Record<string, string | undefined>>) => {
      const next = new URLSearchParams(currentSearch());
      for (const [key, value] of Object.entries(values)) {
        if (value === undefined || value === "") next.delete(key);
        else next.set(key, value);
      }
      write(next);
    },
    [write],
  );

  const clearAll = useCallback(() => {
    write(new URLSearchParams());
  }, [write]);

  return { params: new URLSearchParams(search), setParam, setParams, clearAll };
}
